import React, { useState, useEffect } from 'react';
import { Box, Image, Text, VStack } from '@chakra-ui/react';
import EventsSlide from './GallerySlides/EventsSlide';
import StatsSlide from './GallerySlides/StatsSlide';
import GoalSlide from './GallerySlides/GoalSlide';
import TextSlide from './GallerySlides/TextSlide';

// Helper per generare l'URL del QR code usando l'API di goQR.me
const generateQRCodeUrl = (eventUrl, size = 150) => {
  if (!eventUrl) return null;
  // Codifica l'URL per l'uso nella query string
  const encodedUrl = encodeURIComponent(eventUrl);
  return `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodedUrl}`;
};

const Slideshow = ({ images, interval = 10000 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return undefined;

    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
    }, interval);

    return () => clearInterval(timer);
  }, [images.length, interval]);

  if (!images || images.length === 0) {
    return (
      <Box
        height="100vh"
        width="100vw"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="gray.800"
        color="white"
      >
        <VStack spacing={4}>
          <Text fontSize="2xl" fontWeight="bold">
            Nessuna immagine disponibile
          </Text>
          <Text>La galleria è attualmente vuota.</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      height="100vh"
      width="100vw"
      overflow="hidden"
      bg="black"
    >
      {images.map((image, index) => {
        const isActive = index === currentIndex;
        const slideType = image.slideType || 'image';

        // Slide di tipo eventi
        if (slideType === 'events') {
          // Prepara l'URL dell'immagine di sfondo se presente
          let bgImage = null;
          if (image.media) {
            const mediaUrl = image.media.url || image.media;
            bgImage =
              typeof mediaUrl === 'string'
                ? mediaUrl.startsWith('http')
                  ? mediaUrl
                  : `${window.location.origin}${mediaUrl}`
                : null;

            // Debug log
            if (isActive) {
              console.log('EventsSlide - backgroundImage:', {
                imageMedia: image.media,
                bgImage,
              });
            }
          }

          return (
            <Box
              key={image.id || index}
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              opacity={isActive ? 1 : 0}
              transition="opacity 1s ease-in-out"
              zIndex={isActive ? 1 : 0}
            >
              <EventsSlide backgroundImage={bgImage} title={image.title} />
            </Box>
          );
        }

        // Slide di tipo statistiche
        if (slideType === 'stats') {
          // Prepara l'URL dell'immagine di sfondo se presente
          let bgImage = null;
          if (image.media) {
            const mediaUrl = image.media.url || image.media;
            bgImage =
              typeof mediaUrl === 'string'
                ? mediaUrl.startsWith('http')
                  ? mediaUrl
                  : `${window.location.origin}${mediaUrl}`
                : null;

            // Debug log
            if (isActive) {
              console.log('StatsSlide - backgroundImage:', {
                imageMedia: image.media,
                bgImage,
              });
            }
          }

          return (
            <Box
              key={image.id || index}
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              opacity={isActive ? 1 : 0}
              transition="opacity 1s ease-in-out"
              zIndex={isActive ? 1 : 0}
            >
              <StatsSlide backgroundImage={bgImage} title={image.title} />
            </Box>
          );
        }

        // Slide di tipo goal (donation goal)
        if (slideType === 'goal') {
          // Prepara l'URL dell'immagine di sfondo se presente
          let bgImage = null;
          if (image.media) {
            const mediaUrl = image.media.url || image.media;
            bgImage =
              typeof mediaUrl === 'string'
                ? mediaUrl.startsWith('http')
                  ? mediaUrl
                  : `${window.location.origin}${mediaUrl}`
                : null;

            // Debug log
            if (isActive) {
              console.log('GoalSlide - backgroundImage:', {
                imageMedia: image.media,
                bgImage,
              });
            }
          }

          return (
            <Box
              key={image.id || index}
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              opacity={isActive ? 1 : 0}
              transition="opacity 1s ease-in-out"
              zIndex={isActive ? 1 : 0}
            >
              <GoalSlide
                backgroundImage={bgImage}
                title={image.title}
                donationGoal={image.donationGoal}
              />
            </Box>
          );
        }

        // Slide di tipo text (o slide di tipo image con galleryText)
        if (
          slideType === 'text' ||
          (slideType === 'image' && image.galleryText)
        ) {
          // Prepara l'URL dell'immagine di sfondo se presente
          let bgImage = null;

          // Per slide di tipo image, l'URL potrebbe essere in image.url
          // Per slide di tipo text, l'URL è in image.media.url
          if (slideType === 'image' && image.url) {
            bgImage = image.url.startsWith('http')
              ? image.url
              : `${window.location.origin}${image.url}`;
          } else if (image.media) {
            const mediaUrl = image.media.url || image.media;
            bgImage =
              typeof mediaUrl === 'string'
                ? mediaUrl.startsWith('http')
                  ? mediaUrl
                  : `${window.location.origin}${mediaUrl}`
                : null;
          }

          return (
            <Box
              key={image.id || index}
              position="absolute"
              top={0}
              left={0}
              width="100%"
              height="100%"
              opacity={isActive ? 1 : 0}
              transition="opacity 1s ease-in-out"
              zIndex={isActive ? 1 : 0}
            >
              <TextSlide
                backgroundImage={bgImage}
                galleryText={image.galleryText}
              />
            </Box>
          );
        }

        // Slide di tipo immagine (default)
        return (
          <Box
            key={image.id || index}
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            opacity={isActive ? 1 : 0}
            transition="opacity 1s ease-in-out"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            <Image
              src={image.url}
              alt={image.alt || `Immagine ${index + 1}`}
              objectFit="contain"
              maxWidth="100vh"
              maxHeight="100vw"
              width="auto"
              height="auto"
              bg="black"
              transform="rotate(-90deg)"
              transformOrigin="center"
            />
          </Box>
        );
      })}

      {/* QR Code per l'evento associato o urlLink - solo per slide di tipo immagine */}
      {images[currentIndex]?.slideType === 'image' &&
        (images[currentIndex]?.event?.url || images[currentIndex]?.urlLink) && (
          <Box
            position="absolute"
            top="50%"
            right="28px"
            transform="translateY(-50%)"
            bg="white"
            p={2}
            borderRadius="md"
            boxShadow="lg"
            zIndex={20}
          >
            <Image
              src={generateQRCodeUrl(
                images[currentIndex]?.event?.url ||
                  images[currentIndex]?.urlLink,
                240
              )}
              alt="QR Code"
              width="110px"
              height="110px"
              onError={(e) => {
                console.error('Errore nel caricamento QR code:', e);
                const qrUrl =
                  images[currentIndex]?.event?.url ||
                  images[currentIndex]?.urlLink;
                console.log('URL:', qrUrl);
                console.log('URL QR code:', generateQRCodeUrl(qrUrl, 240));
              }}
            />
          </Box>
        )}
    </Box>
  );
};

export default Slideshow;
